import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const API = ""; // same-origin now — Vercel rewrites /api/* to the backend (see client/vercel.json). Keeps the session cookie first-party for Safari/Firefox.

/* ─── Helpers ─── */
function timeAgo(date) {
  if (!date) return "Never";
  const diff = Date.now() - new Date(date).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function planColor(plan) {
  if (plan === "ultra_pro") return "#FFD700";
  if (plan === "pro")       return "#FF6B00";
  return "#555";
}
function planLabel(plan) {
  if (plan === "ultra_pro") return "🌟 Ultra Pro";
  if (plan === "pro")       return "⚡ Pro";
  return "🆓 Free";
}

/* ─── Stat Card ─── */
function StatCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: "#0a1520",
      border: `1px solid ${color}33`,
      borderRadius: 14,
      padding: "16px 20px",
      flex: 1,
      minWidth: 130,
      boxShadow: `0 0 20px ${color}11`,
    }}>
      <div style={{ color, fontSize: 26, fontWeight: 900, fontFamily: "monospace" }}>{value}</div>
      <div style={{ color: "#aaa", fontSize: 11, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ color: "#444", fontSize: 10, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* ─── Grant Modal ─── */
function GrantModal({ user, onClose, onSuccess, token }) {
  const [plan, setPlan]   = useState("pro");
  const [days, setDays]   = useState(1);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg]     = useState("");

  const grant = async () => {
    setLoading(true);
    setMsg("");
    try {
      const res  = await fetch(`${API}/api/admin/grant-premium`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ email: user.email, plan, days }),
      });
      const data = await res.json();
      if (!res.ok) { setMsg("❌ " + data.message); return; }
      setMsg(data.message);
      setTimeout(() => { onSuccess(); onClose(); }, 1200);
    } catch {
      setMsg("❌ Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 20,
    }}>
      <div style={{
        background: "#050f1a", border: "1px solid #FFD70044",
        borderRadius: 18, padding: 28, width: "100%", maxWidth: 340,
        fontFamily: "monospace",
      }}>
        <h3 style={{ color: "#FFD700", margin: "0 0 6px", fontSize: 16 }}>👑 Premium Grant</h3>
        <p style={{ color: "#666", fontSize: 12, margin: "0 0 20px" }}>{user.email}</p>

        {/* Plan selector */}
        <label style={{ color: "#888", fontSize: 11, display: "block", marginBottom: 6 }}>Plan</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { value: "pro",       label: "⚡ Pro" },
            { value: "ultra_pro", label: "🌟 Ultra Pro" },
          ].map(p => (
            <button
              key={p.value}
              onClick={() => setPlan(p.value)}
              style={{
                flex: 1, padding: "10px 8px",
                border: `1px solid ${plan === p.value ? "#FFD700" : "#1a2a3a"}`,
                borderRadius: 10, cursor: "pointer",
                background: plan === p.value ? "#FFD70018" : "transparent",
                color: plan === p.value ? "#FFD700" : "#555",
                fontWeight: plan === p.value ? 700 : 400,
                fontSize: 13, fontFamily: "monospace",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Duration selector */}
        <label style={{ color: "#888", fontSize: 11, display: "block", marginBottom: 6 }}>Duration</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {[
            { value: 1,  label: "1 Din" },
            { value: 60, label: "60 Din" },
          ].map(d => (
            <button
              key={d.value}
              onClick={() => setDays(d.value)}
              style={{
                flex: 1, padding: "10px 8px",
                border: `1px solid ${days === d.value ? "#00cfff" : "#1a2a3a"}`,
                borderRadius: 10, cursor: "pointer",
                background: days === d.value ? "#00cfff18" : "transparent",
                color: days === d.value ? "#00cfff" : "#555",
                fontWeight: days === d.value ? 700 : 400,
                fontSize: 13, fontFamily: "monospace",
              }}
            >
              {d.label}
            </button>
          ))}
        </div>

        {msg && (
          <div style={{ background: "#0a1520", borderRadius: 8, padding: "8px 12px", marginBottom: 14, color: msg.startsWith("❌") ? "#ff4444" : "#00C896", fontSize: 12 }}>
            {msg}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={grant}
            disabled={loading}
            style={{
              flex: 1, padding: 12,
              background: loading ? "#333" : "linear-gradient(135deg,#FFD700,#FF6B00)",
              border: "none", borderRadius: 10, cursor: loading ? "not-allowed" : "pointer",
              color: "#000", fontWeight: 800, fontSize: 14, fontFamily: "monospace",
            }}
          >
            {loading ? "..." : "✅ Grant karo"}
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: 12,
              background: "transparent", border: "1px solid #1a2a3a",
              borderRadius: 10, cursor: "pointer", color: "#555",
              fontSize: 14, fontFamily: "monospace",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Revoke Confirm ─── */
function RevokeConfirm({ user, onClose, onSuccess, token }) {
  const [loading, setLoading] = useState(false);

  const revoke = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/admin/revoke-premium`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ email: user.email }),
      });
      if (res.ok) { onSuccess(); onClose(); }
    } catch {}
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div style={{ background: "#050f1a", border: "1px solid #ff444433", borderRadius: 18, padding: 28, width: "100%", maxWidth: 300, fontFamily: "monospace" }}>
        <h3 style={{ color: "#ff4444", margin: "0 0 8px" }}>⚠️ Premium Revoke?</h3>
        <p style={{ color: "#666", fontSize: 12, margin: "0 0 20px" }}>{user.email} ko free kar doge?</p>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={revoke} disabled={loading}
            style={{ flex: 1, padding: 12, background: "linear-gradient(135deg,#ff4444,#cc0000)", border: "none", borderRadius: 10, cursor: "pointer", color: "#fff", fontWeight: 700, fontFamily: "monospace" }}>
            {loading ? "..." : "Haan, Revoke"}
          </button>
          <button onClick={onClose}
            style={{ flex: 1, padding: 12, background: "transparent", border: "1px solid #1a2a3a", borderRadius: 10, cursor: "pointer", color: "#555", fontFamily: "monospace" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Send Notification (admin broadcasts to all users) ─── */
function SendNotification({ token }) {
  const [message,     setMessage]     = useState("");
  const [sending,     setSending]     = useState(false);
  const [msg,         setMsg]         = useState("");
  const [history,     setHistory]     = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API}/api/admin/notifications`, { headers: { Authorization: "Bearer " + token } });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.notifications || []);
      }
    } catch {}
  };

  useEffect(() => { if (showHistory) loadHistory(); }, [showHistory]);

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    setMsg("");
    try {
      const res  = await fetch(`${API}/api/admin/notifications`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ message: message.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg("✅ Sabko bhej diya!");
        setMessage("");
        if (showHistory) loadHistory();
      } else {
        setMsg("❌ " + (data.message || "Failed"));
      }
    } catch {
      setMsg("❌ Network error");
    }
    setSending(false);
  };

  const deleteOne = async (id) => {
    try {
      await fetch(`${API}/api/admin/notifications/${id}`, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token },
      });
      setHistory(h => h.filter(n => n._id !== id));
    } catch {}
  };

  const disabled = sending || !message.trim();

  return (
    <div style={{ background: "#0a1520", border: "1px solid #FFD70033", borderRadius: 14, padding: 18, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: "#FFD700", fontSize: 14 }}>📢 Send Notification</h3>
        <button onClick={() => setShowHistory(s => !s)} style={{ background: "none", border: "1px solid #1a2a3a", borderRadius: 8, color: "#555", fontSize: 10, padding: "5px 10px", cursor: "pointer", fontFamily: "monospace" }}>
          {showHistory ? "Hide History" : "History"}
        </button>
      </div>

      <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="Sabhi users ko kya message bhejna hai..."
        maxLength={2000}
        rows={3}
        style={{
          width: "100%", padding: 12, background: "#050f1a",
          border: "1px solid #00cfff22", borderRadius: 10,
          color: "#00cfff", fontSize: 12.5, fontFamily: "monospace",
          resize: "vertical", outline: "none", boxSizing: "border-box",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
        <span style={{ color: "#333", fontSize: 10 }}>{message.length}/2000</span>
        <button
          onClick={send}
          disabled={disabled}
          style={{
            padding: "9px 18px",
            background: disabled ? "#222" : "linear-gradient(135deg,#FFD700,#FF6B00)",
            border: "none", borderRadius: 8,
            color: disabled ? "#555" : "#000",
            fontWeight: 800, fontSize: 12, cursor: disabled ? "not-allowed" : "pointer",
            fontFamily: "monospace",
          }}
        >
          {sending ? "..." : "🚀 Sabko Bhejo"}
        </button>
      </div>

      {msg && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: msg.startsWith("❌") ? "#ff4444" : "#00C896" }}>{msg}</div>
      )}

      {showHistory && (
        <div style={{ marginTop: 16, borderTop: "1px solid #1a2a3a", paddingTop: 12, maxHeight: 260, overflowY: "auto" }}>
          {history.length === 0 && <div style={{ color: "#333", fontSize: 11 }}>Koi notification bheji nahi hai abhi tak.</div>}
          {history.map(n => (
            <div key={n._id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #0d1a26" }}>
              <span style={{ fontSize: 13 }}>{n.type === "system" ? "🚀" : "📢"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#ccc", fontSize: 11.5, lineHeight: 1.4 }}>{n.message}</div>
                <div style={{ color: "#333", fontSize: 9.5, marginTop: 2 }}>
                  {n.createdBy} · {timeAgo(n.createdAt)}
                </div>
              </div>
              {n.type === "admin" && (
                <button onClick={() => deleteOne(n._id)} style={{ background: "none", border: "none", color: "#ff444488", cursor: "pointer", fontSize: 13 }}>🗑</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN ADMIN PAGE
═══════════════════════════════════════════════════ */
export default function AdminPage() {
  const navigate  = useNavigate();
  const token     = localStorage.getItem("subai_session"); // non-sensitive "am I logged in" flag now — real session is the httpOnly cookie

  const [stats,       setStats]       = useState(null);
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [filter,      setFilter]      = useState("all"); // all | online | pro | ultra_pro | free
  const [grantUser,   setGrantUser]   = useState(null);
  const [revokeUser,  setRevokeUser]  = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        fetch(`${API}/api/admin/stats`, { headers: { Authorization: "Bearer " + token } }),
        fetch(`${API}/api/admin/users`, { headers: { Authorization: "Bearer " + token } }),
      ]);

      if (statsRes.status === 403 || usersRes.status === 403) {
        // Not admin → redirect
        navigate("/dashboard");
        return;
      }
      if (statsRes.status === 401) { navigate("/"); return; }

      const statsData = await statsRes.json();
      const usersData = await usersRes.json();
      setStats(statsData);
      setUsers(usersData.users || []);
      setLastRefresh(new Date());
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate("/"); return; }
    load();
  }, []);

  // Auto-refresh every 30s
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  /* ── Filtered users ── */
  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());

    const matchFilter =
      filter === "all"       ? true :
      filter === "online"    ? u.online :
      filter === "pro"       ? u.plan === "pro" :
      filter === "ultra_pro" ? u.plan === "ultra_pro" :
      filter === "free"      ? u.plan === "free" : true;

    return matchSearch && matchFilter;
  });

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#020c14", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace" }}>
      <div style={{ color: "#00cfff", fontSize: 14 }}>Loading admin panel...</div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#020c14", color: "#00cfff", fontFamily: "monospace", padding: "20px 16px 60px" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>
            <span style={{ color: "#fff" }}>Sub</span>
            <span style={{ background: "linear-gradient(90deg,#FF6B00,#FFB347)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>AI</span>
            <span style={{ color: "#FFD700", marginLeft: 10, fontSize: 14 }}>Admin 👑</span>
          </h1>
          {lastRefresh && (
            <div style={{ color: "#333", fontSize: 10, marginTop: 3 }}>
              Last refresh: {lastRefresh.toLocaleTimeString()} · Auto-refreshes every 30s
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} style={{ padding: "8px 14px", background: "#0a1520", border: "1px solid #00cfff33", borderRadius: 8, color: "#00cfff", cursor: "pointer", fontSize: 11 }}>
            🔄 Refresh
          </button>
          <button onClick={() => navigate("/dashboard")} style={{ padding: "8px 14px", background: "transparent", border: "1px solid #1a2a3a", borderRadius: 8, color: "#555", cursor: "pointer", fontSize: 11 }}>
            ← Dashboard
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <>
          <h3 style={{ margin: "0 0 10px", color: "#00cfff", fontSize: 13, opacity: 0.85 }}>📊 Overview</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24 }}>
            <StatCard label="Total Users"   value={stats.totalUsers}   color="#00cfff" />
            <StatCard label="Online Now"    value={stats.onlineUsers}  color="#00C896" sub="last 5 min" />
            <StatCard label="Total Files"   value={stats.totalUploads} color="#7B61FF" />
            <StatCard label="⚡ Pro"        value={stats.proUsers}     color="#FF6B00" />
            <StatCard label="🌟 Ultra Pro"  value={stats.ultraUsers}   color="#FFD700" />
            <StatCard label="🆓 Free"       value={stats.freeUsers}    color="#444" />
          </div>
        </>
      )}

      {/* Send Notification */}
      <SendNotification token={token} />

      {/* Search + Filter */}
      <h3 style={{ margin: "0 0 10px", color: "#00cfff", fontSize: 13, opacity: 0.85 }}>👥 Users</h3>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search name ya email..."
          style={{
            flex: 1, minWidth: 180, padding: "10px 14px",
            background: "#0a1520", border: "1px solid #00cfff22",
            borderRadius: 10, color: "#00cfff", fontSize: 12,
            fontFamily: "monospace", outline: "none",
          }}
        />
        {["all","online","pro","ultra_pro","free"].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "8px 12px", borderRadius: 8, fontSize: 11, cursor: "pointer",
              border: `1px solid ${filter === f ? "#FFD700" : "#1a2a3a"}`,
              background: filter === f ? "#FFD70018" : "transparent",
              color: filter === f ? "#FFD700" : "#555",
              fontWeight: filter === f ? 700 : 400,
            }}
          >
            {f === "ultra_pro" ? "Ultra Pro" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div style={{ color: "#333", fontSize: 11, marginBottom: 12 }}>
        {filtered.length} users dikh rahe hain
      </div>

      {/* Users List */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.map(u => (
          <div key={u.id} style={{
            background: "#050f1a",
            border: `1px solid ${u.online ? "#00C89633" : "#0d2035"}`,
            borderRadius: 14, padding: "14px 16px",
            boxShadow: u.online ? "0 0 12px #00C89611" : "none",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
              {/* User info */}
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>{u.name || "No name"}</span>
                  {u.online && <span style={{ background: "#00C89622", color: "#00C896", fontSize: 9, padding: "2px 7px", borderRadius: 20, fontWeight: 700 }}>● ONLINE</span>}
                  {u.isAdmin && <span style={{ background: "#FFD70022", color: "#FFD700", fontSize: 9, padding: "2px 7px", borderRadius: 20, fontWeight: 700 }}>👑 ADMIN</span>}
                </div>
                <div style={{ color: "#4a7a9b", fontSize: 11, marginTop: 3 }}>{u.email}</div>
                <div style={{ display: "flex", gap: 12, marginTop: 5, flexWrap: "wrap" }}>
                  <span style={{ color: planColor(u.plan), fontSize: 11, fontWeight: 700 }}>
                    {planLabel(u.plan)}
                    {u.planExpiry && u.plan !== "free" && (
                      <span style={{ color: "#555", fontWeight: 400 }}>
                        {" "}· {new Date(u.planExpiry) > new Date()
                          ? `${Math.ceil((new Date(u.planExpiry) - Date.now()) / 86400000)}d baki`
                          : "Expired"}
                      </span>
                    )}
                  </span>
                  <span style={{ color: "#333", fontSize: 10 }}>Last seen: {timeAgo(u.lastSeen)}</span>
                  <span style={{ color: "#333", fontSize: 10 }}>Joined: {new Date(u.joinedAt).toLocaleDateString("en-IN")}</span>
                </div>
              </div>

              {/* Action buttons */}
              {!u.isAdmin && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    onClick={() => setGrantUser(u)}
                    style={{
                      padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                      background: "linear-gradient(135deg,#FFD70022,#FF6B0022)",
                      border: "1px solid #FFD70044", color: "#FFD700",
                      fontSize: 12, fontFamily: "monospace", fontWeight: 700,
                    }}
                  >
                    👑 Grant
                  </button>
                  {u.plan !== "free" && (
                    <button
                      onClick={() => setRevokeUser(u)}
                      style={{
                        padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                        background: "transparent", border: "1px solid #ff444433",
                        color: "#ff6666", fontSize: 12, fontFamily: "monospace",
                      }}
                    >
                      ✕ Revoke
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={{ textAlign: "center", color: "#333", padding: 40, fontSize: 13 }}>
            Koi user nahi mila 🔍
          </div>
        )}
      </div>

      {/* Modals */}
      {grantUser  && <GrantModal  user={grantUser}  token={token} onClose={() => setGrantUser(null)}  onSuccess={load} />}
      {revokeUser && <RevokeConfirm user={revokeUser} token={token} onClose={() => setRevokeUser(null)} onSuccess={load} />}

      <style>{`
        input::placeholder { color: #1a3a4a; }
        input:focus { border-color: #00cfff55 !important; }
      `}</style>
    </div>
  );
}
